import { useMemo } from 'react';
import { AuthRoles } from 'skipify-types';

import { useIsSkipifyAdmin } from '../../hooks';
import type { Auth0RoleSummary } from '../../store/modules/merchant/types';

/** Plain-language portal user roles for MMS create/update (Auth0 role `name` values). */
export type PortalUserRoleCatalogEntry = {
  name: string;
  label: string;
  description: string;
};

/** UI list order: org owner first, then each group least privilege → most. */
const PORTAL_USER_ROLE_CATALOG: readonly PortalUserRoleCatalogEntry[] = [
  {
    name: AuthRoles.ORGANIZATION_OWNER,
    label: 'Organization owner',
    description:
      'Merchant settings — admin and insights for assigned merchants, plus managing portal users in that scope.',
  },
  {
    name: AuthRoles.MERCHANT_INSIGHTS_VIEWER,
    label: 'Merchant insights',
    description: 'View analytics and reporting for assigned merchants.',
  },
  {
    name: AuthRoles.AGENT_HUB_VIEWER,
    label: 'Agent Hub — read only',
    description: 'View Agent Hub settings and metrics without making changes.',
  },
  {
    name: AuthRoles.AGENT_HUB_ADMIN,
    label: 'Agent Hub — admin',
    description: 'View and change Agent Hub settings.',
  },
  {
    name: AuthRoles.MERCHANT_VIEWER,
    label: 'Merchant settings — read only',
    description: 'View merchant configuration without making changes.',
  },
  {
    name: AuthRoles.MERCHANT_ADMIN,
    label: 'Merchant settings — admin',
    description: 'Create, read, update, and delete merchant configuration for assigned merchants.',
  },
  {
    name: AuthRoles.CHILD_MERCHANT_EDITOR,
    label: 'Child merchants — editor',
    description:
      'View merchants and update child merchants under assigned parents. In Merchant access, assign the parent merchant directly so they can access its children in the portal. Cannot be combined with Merchant settings roles.',
  },
  {
    name: AuthRoles.CHILD_MERCHANT_ADMIN,
    label: 'Child merchants — admin',
    description:
      'Create and manage child merchants under assigned parents. In Merchant access, assign the parent merchant directly so they can access and manage its children in the portal. Cannot be combined with Merchant settings roles.',
  },
  {
    name: AuthRoles.PAYLINK_BASIC_USER,
    label: 'Paylink — send only',
    description: 'Send paylinks and create basic paylink customer records.',
  },
  {
    name: AuthRoles.PAYLINK_STANDARD_USER,
    label: 'Paylink — manage',
    description: 'Manage paylinks and customers, excluding refunds and downloads.',
  },
  {
    name: AuthRoles.PAYLINK_SUPER_USER,
    label: 'Paylink — full access',
    description: 'Full paylink operations including refunds and downloads.',
  },
  {
    name: AuthRoles.MIT_STANDARD_USER,
    label: 'Card on file — own records',
    description: 'Saved cards and paylinks limited to this user’s own activity (own records only).',
  },
  {
    name: AuthRoles.MIT_SUPER_USER,
    label: 'Card on file — all records',
    description:
      'Charge, view, and refund saved cards across the merchant, including Paylink — full access.',
  },
  {
    name: AuthRoles.SKIPIFY_VIEWER,
    label: 'Skipify viewer',
    description: 'Read-only internal access across merchants.',
  },
  {
    name: AuthRoles.SKIPIFY_ADMIN,
    label: 'Skipify admin',
    description: 'Full internal access across all merchants and portal administration.',
  },
];

const SKIPIFY_ONLY_ROLE_NAMES = new Set<string>([
  AuthRoles.SKIPIFY_ADMIN,
  AuthRoles.SKIPIFY_VIEWER,
]);

function catalogEntryToAuth0RoleSummary(entry: PortalUserRoleCatalogEntry): Auth0RoleSummary {
  return {
    id: entry.name,
    name: entry.name,
    description: entry.description,
  };
}

/** Assignable roles for the current actor (Skipify admins see internal roles). */
export function getAssignablePortalUserRoles(isSkipifyAdmin: boolean): Auth0RoleSummary[] {
  const entries = isSkipifyAdmin
    ? PORTAL_USER_ROLE_CATALOG
    : PORTAL_USER_ROLE_CATALOG.filter((entry) => !SKIPIFY_ONLY_ROLE_NAMES.has(entry.name));
  return entries.map(catalogEntryToAuth0RoleSummary);
}

export function useAssignablePortalUserRoles(): Auth0RoleSummary[] {
  const isSkipifyAdmin = useIsSkipifyAdmin();
  return useMemo(() => getAssignablePortalUserRoles(isSkipifyAdmin), [isSkipifyAdmin]);
}

/** Display label for a role name in tables and chips. */
export function getPortalUserRoleLabel(roleName: string): string {
  const entry = PORTAL_USER_ROLE_CATALOG.find((r) => r.name === roleName);
  return entry?.label ?? roleName;
}

export function getPortalUserRoleCatalogForReference(
  isSkipifyAdmin: boolean,
): PortalUserRoleCatalogEntry[] {
  return isSkipifyAdmin
    ? [...PORTAL_USER_ROLE_CATALOG]
    : PORTAL_USER_ROLE_CATALOG.filter((entry) => !SKIPIFY_ONLY_ROLE_NAMES.has(entry.name));
}
